/* oxlint-disable */
'use client';

import {
  memo, useCallback, useEffect, useRef, useState,
} from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import emailSpellChecker from '@zootools/email-spell-checker';
import { Eye, EyeOff } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Vendors } from '@/enums/core';

import { messages } from '@/features/auth/localization/texts';
import { useRegister } from '@/features/v2/auth/register/hooks/useRegister';

import PasswordStrengthMeter from '@/features/validatePassword/PasswordStrengthMeter';
import { userUIStore } from '@/stores/userUIStore';

import { HButton } from '@/stories/atoms/buttons/mainButton/Button';
import { HInput } from '@/stories/atoms/inputs/Input';
import { PhoneCountry } from '@/stories/atoms/PhoneCountry/PhoneCountry';

import { getCurrencyByCountry } from '@/utils/currency';

import { useAuth } from '@/core/auth/client/AuthProvider';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Form,
  FormControl, FormField, FormItem, FormLabel, FormMessage,
} from '@/components/ui/form';

import { loginEmailAndPass, registerEmailAndPass } from '../schema';

interface Props {
  isRegister: boolean;
  onSuccess?: () => void | Promise<void>;
  onSuccessWithOtp?: (email: string) => void | Promise<void>;
  onError: (error: string, code?: string) => void;
  onBeforeRegister?: () => Promise<string | null>;
  country?: string; // Optional country code (GT, DO, GLOBAL, etc.)
  gift?: string; // Welcome bonus: 'casino' or 'sports'
  parent?: number; // Parent/referrer ID
  affiliatorId?: string; // Affiliate code
  partner?: string; // Partner reference
  landing?: number; // Landing page ID
  vendor?: number; // Vendor type
  clickId?: string; // Click ID for tracking
  phone?: string; // Phone number
  onCountryChange?: (countryCode: string) => void;
}

// TODO: Refactroing is required. And split the form into register and login.
// A lot of useStates. Make one common form state.

const FormEmailAndPasswordBetterAuth = ({
  isRegister, onSuccess = () => {}, onSuccessWithOtp = () => {}, onError, onBeforeRegister, country = '', gift = '', parent = 0, affiliatorId = '', partner = '', landing = 0, vendor = Vendors.EMAIL, clickId = '', phone = '', onCountryChange,
}: Props) => {
  const { toggleCheckbox } = userUIStore();
  const { signIn, loading } = useAuth();
  const { register } = useRegister();
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isValidatingEmail, setIsValidatingEmail] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [emailValidationError, setEmailValidationError] = useState<string | null>(null);
  const [showValidation, setShowValidation] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<string>(country || '');

  // Sync with prop when it changes
  useEffect(() => {
    if (country) {
      setSelectedCountry(country);
    }
  }, [country]);

  const registerSchema = registerEmailAndPass.extend({
    ageVerification: z.boolean().refine((val) => { return val === true; }, {
      message: 'Debes verificar tu edad para continuar',
    }),
  });

  const formSchema = isRegister ? registerSchema : loginEmailAndPass;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
      ...(isRegister && { ageVerification: false }),
    },
    mode: 'onChange',
  });

  const validateEmailWithZeroBounce = async (email: string) => {
    try {
      setIsValidatingEmail(true);
      setEmailValidationError(null);
      form.clearErrors('email');

      const response = await fetch('/api/validate-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!data.success) {
        console.warn('Email validation service unavailable:', data.message);
        return true;
      }

      if (data.isDisposable) {
        const reason = 'No se permiten correos electrónicos temporales o desechables';
        setEmailValidationError(reason);
        form.setError('email', { type: 'manual', message: reason });
        return false;
      }

      if (!data.isValid) {
        const reason = data.result?.status === 'invalid' ? 'Correo electrónico inválido' : data.result?.status === 'catch-all' ? 'Correo electrónico inválido' : data.result?.status === 'unknown' ? 'No se puede verificar el correo electrónico' : 'Correo electrónico inválido';
        setEmailValidationError(reason);
        form.setError('email', { type: 'manual', message: reason });
        return false;
      }

      return true;
    } catch (error) {
      console.error('Email validation error:', error);
      return true;
    } finally {
      setIsValidatingEmail(false);
    }
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    // Sync guard: React state updates are async, so without a ref double-clicks slip through.
    if (isSubmittingRef.current || isValidatingEmail) return;

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      if (isRegister) {
        const isEmailValid = await validateEmailWithZeroBounce(values.email);
        if (!isEmailValid) return;

        const visitorId = onBeforeRegister ? await onBeforeRegister() : null;

        // Derive currency from country code
        const derivedCurrency = getCurrencyByCountry(selectedCountry);

        console.warn('[FormEmailAndPassword] Registering with:', {
          country: selectedCountry,
          currency: derivedCurrency,
          email: values.email,
        });

        // Register with Better Auth
        const result = await register({
          email: values.email,
          password: values.password,
          name: '', // You can add name field if needed
          remember: true,
          currency: derivedCurrency,
          country: selectedCountry, // Country code (GT, DO, GLOBAL, etc.)
          gift,
          parent,
          affiliatorId,
          partner,
          landing,
          vendor,
          clickId,
          phone,
          visitorId,
        });

        // If registration successful and OTP callback provided, trigger OTP flow
        if (result && !result.error) {
          if (onSuccessWithOtp) {
            // Await so the submit lock covers post-signup prep (fingerprint skip / OTP sheet).
            await onSuccessWithOtp(values.email);
            return;
          }
        }
      } else await signIn(values.email, values.password, true);

      // Success callback
      if (onSuccess) await onSuccess();
    } catch (error: any) {
      console.error('Auth error:', error);
      if (onError) {
        onError(error.message || (isRegister ? 'Registration failed' : 'Login failed'), error?.code);
      }
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleEmailBlur = useCallback(() => {
    const email = form.getValues('email');
    if (email && typeof email === 'string' && email.trim()) {
      const result = emailSpellChecker.run({ email });
      setSuggestion(result?.full ?? null);
    }
    form.trigger('email');
  }, [form]);

  return (
    <div className="flex flex-col gap-2 w-full">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 w-full mb-6">
          {isRegister && (
            <div className="space-y-2">
              <FormLabel className="text-sivarbet-text text-sm">País / Moneda</FormLabel>
              <PhoneCountry
                selectedCountry={selectedCountry}
                onSelectCountry={(code) => {
                  setSelectedCountry(code);
                  if (onCountryChange) {
                    onCountryChange(code);
                  }
                }}
                autoDetect
                theme="dark"
                height="thin"
              />
            </div>
          )}
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormControl>
                    <HInput
                      placeholder={messages.email}
                      value={field.value}
                      name={field.name}
                      type="email"
                      onChange={(e) => {
                        const lowercaseEmail = e.target.value.toLowerCase();
                        form.setValue('email', lowercaseEmail);
                        if (emailValidationError) {
                          setEmailValidationError(null);
                          form.clearErrors('email');
                        }
                      }}
                      onBlur={(e) => {
                        form.register('email').onBlur(e);
                        handleEmailBlur();
                      }}
                      height="thin"
                      theme="dark"
                    />
                  </FormControl>
                  <FormMessage />
                  {emailValidationError && (
                    <div className="mt-2 p-2 bg-red-50 rounded-lg text-center">
                      <p className="text-red-600 font-medium">{emailValidationError}</p>
                    </div>
                  )}
                  {suggestion && (
                    <div className="mt-2 p-2 bg-amber-50 rounded-lg text-center">
                      <p className="text-red-600 font-medium">
                        ¿Quieres decir
                        {' '}
                        <button
                          type="button"
                          className="font-bold text-red-700 underline"
                          onClick={() => {
                            form.setValue('email', suggestion);
                            setSuggestion(null);
                            form.clearErrors('email');
                            setEmailValidationError(null);
                          }}
                        >
                          {suggestion}
                        </button>
                        ?
                      </p>
                    </div>
                  )}
                </FormItem>
              );
            }}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormControl>
                    <div className="relative">
                      <HInput
                        placeholder={messages.password}
                        value={field.value}
                        name={field.name}
                        type={showPassword ? 'text' : 'password'}
                        height="thin"
                        theme="dark"
                        onChange={(e) => {
                          field.onChange(e);
                          if (isRegister) setShowValidation(true);
                        }}
                        onBlur={() => {
                          field.onBlur();
                          if (isRegister) setShowValidation(false);
                        }}
                      />
                      <button
                        type="button"
                        aria-label={showPassword ? messages.hidePassword : messages.showPassword}
                        onClick={() => { return setShowPassword(!showPassword); }}
                        className="absolute right-3 top-1/2 inline-flex size-6 -translate-y-1/2 items-center justify-center"
                      >
                        {showPassword ? <EyeOff size={20} aria-hidden /> : <Eye size={20} aria-hidden />}
                      </button>
                      {isRegister && showValidation && (
                        <div className="bg-sivarbet-text p-4 rounded-xl text-sm absolute top-11 right-1 min-w-12 z-100">
                          <PasswordStrengthMeter password={field.value} />
                          <div className="absolute w-0 h-0 border-solid -top-2 left-3.5 border-l-1.5 border-l-transparent border-r-2.5 border-r-transparent border-b-2.5 border-b-sivarbet-text" />
                        </div>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
          {isRegister && (
            <FormField
              control={form.control}
              name={'ageVerification' as any}
              render={({ field }) => {
                return (
                  <FormItem className="w-full flex flex-row items-center justify-center">
                    <FormControl className="mr-4">
                      <Checkbox
                        onCheckedChange={field.onChange}
                        className="bg-sivarbet-primary! scale-150"
                        checked={Boolean(field.value)}
                        name={field.name}
                        ref={field.ref}
                        onBlur={field.onBlur}
                        onClick={() => {
                          toggleCheckbox(!field.value);
                          field.onChange(!field.value);
                        }}
                      />
                    </FormControl>
                    <FormLabel>{messages.messagesRegister}</FormLabel>
                  </FormItem>
                );
              }}
            />
          )}

          <div className="flex flex-col gap-1">
            <HButton
              mode="primary"
              size="large"
              label={
                isValidatingEmail
                  ? 'Validando...'
                  : (loading || isSubmitting)
                    ? (isRegister ? 'Registrando...' : 'Iniciando sesión...')
                    : isRegister ? messages.register : messages.login
              }
              disabled={loading || isValidatingEmail || isSubmitting || !form.formState.isValid}
              type="submit"
            />
          </div>
        </form>
      </Form>
    </div>
  );
};

export default memo(FormEmailAndPasswordBetterAuth);
